import { SkeletonLoader } from '@freelancelabs/components';

/**
 * LES FORMES DESSINÉES PENDANT UNE ATTENTE, mutualisées (PCG-306).
 *
 * Le back-office écrivait « Chargement… » à neuf endroits. Une phrase ne dit rien de ce
 * qui vient et n'occupe pas la place de ce qui vient : le bloc grandit d'un coup à
 * l'arrivée de la réponse, au moment précis où l'œil s'y pose.
 *
 * Elles vivent ici et non dans le design system parce qu'elles n'ajoutent aucun style :
 * elles assemblent `SkeletonLoader` avec des utilitaires de mise en page, ce que la règle
 * du dépôt autorise à l'application. Le jour où une troisième application en a besoin,
 * c'est le moment de les promouvoir, pas avant.
 */

interface ListProps {
    /** Combien de lignes se dessinent. À régler sur ce que la liste rend d'ordinaire. */
    rows?: number;
    /**
     * Une case se dessine à gauche de chaque ligne, pour une liste qu'on coche. Sans
     * elle, les lignes se décaleraient toutes à l'arrivée des cases.
     */
    withControl?: boolean;
    className?: string;
}

export const ListSkeleton = ({ rows = 4, withControl = false, className }: ListProps) => (
    <ul className={`flex flex-col ${className ?? ''}`} aria-busy="true">
        {Array.from({ length: rows }, (_, rang) => (
            <li
                className="flex items-center gap-2.5 border-b border-border-norm px-2.5 py-2 last:border-b-0"
                key={rang}
            >
                {withControl && <SkeletonLoader width={16} height={16} />}
                {/* Les largeurs ALTERNENT au lieu d'être toutes égales : une pile de barres
                    identiques se lit comme un tableau, pas comme une liste de noms. Elles
                    dépendent du rang et non du hasard, sinon un re-rendu fait gigoter
                    l'attente, ce qui se lit comme une donnée qui change. */}
                <SkeletonLoader className={rang % 3 === 0 ? 'w-1/2' : rang % 3 === 1 ? 'w-2/3' : 'w-2/5'} height={14} />
                <SkeletonLoader className="ml-auto" width={56} height={12} />
            </li>
        ))}
    </ul>
);

interface MatrixProps {
    /** Combien de groupes de droits se dessinent. */
    groups?: number;
    /** Combien de droits par groupe. */
    rowsPerGroup?: number;
}

/**
 * La matrice des droits, groupée par module. Le nombre de groupes n'est PAS inventé :
 * le référentiel en porte sept, et un squelette qui en montrerait trois ferait sauter la
 * carte de quatre groupes à l'arrivée de la réponse.
 */
export const MatrixSkeleton = ({ groups = 7, rowsPerGroup = 3 }: MatrixProps) => (
    <div className="flex flex-col gap-2" aria-busy="true">
        {Array.from({ length: groups }, (_, groupe) => (
            <div className="rounded-md border border-border-norm" key={groupe}>
                <div className="flex items-center gap-2.5 border-b border-border-norm bg-background-weak px-3 py-2.5">
                    <SkeletonLoader className="w-1/4" height={14} />
                    <SkeletonLoader className="ml-auto" width={40} height={12} />
                </div>
                <ul className="flex flex-col px-3 py-1.5">
                    {Array.from({ length: rowsPerGroup }, (_, ligne) => (
                        <li className="flex items-center gap-2.5 py-1.5" key={ligne}>
                            <SkeletonLoader width={16} height={16} />
                            <SkeletonLoader className={ligne % 2 === 0 ? 'w-2/5' : 'w-1/2'} height={12} />
                        </li>
                    ))}
                </ul>
            </div>
        ))}
    </div>
);
